import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { SwPush } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import { CapacitorNfc } from '@capgo/capacitor-nfc';
import { QRScanner } from '@guardtracker/qr-scanner';
import { Html5Qrcode } from 'html5-qrcode';
import { firstValueFrom } from 'rxjs';
import {
  DIALOG_DATA,
  getDistanceUsingHaversine,
  getInstanceRegion,
  showGeoFenceAlert,
  sortArrayOfObject,
} from '../../../../global.variable';
import { AppService } from '../../../app.service';
import { CheckpointService } from '../../../pages/pages/checkpoints/checkpoint.service';
import { ClientsService } from '../../../pages/pages/clients/clients.service';
import { JobsService } from '../../../pages/pages/jobs/jobs.service';
import { PagesService } from '../../../pages/pages/pages.service';
import { DataCheckService } from '../../services/data-check.service';
import { gTDB } from '../../services/db';
import { DeviceInfoService } from '../../services/device-info.service';
import { LoadingSpinnerService } from '../../services/loading-spinner.service';
import { ToasterService } from '../../services/toaster.service';
import { AddEditSiteComponent } from '../add-edit-site/add-edit-site.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { DialogRef } from '../modal-dialogue/modal-dialogue.ref';
import { ModalDialogueService } from '../modal-dialogue/modal-dialogue.service';
import { PrompterComponent } from '../prompter/prompter.component';
import { ScreenshotGuideComponent } from '../screenshot-guide/screenshot-guide.component';
import { SplashScreenComponent } from '../splash-screen/splash-screen.component';
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from 'capacitor-native-settings';
import { SqliteDbService } from '../../services/sqlite-db.service';

declare var bootstrap: any;
@Component({
  selector: 'gtapp-scan-qr-nfc',
  templateUrl: './scan-qr-nfc.component.html',
  styleUrl: './scan-qr-nfc.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ScanQrNfcComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('qrScanWarningTemplate') qrScanWarningTemplate: any =
    TemplateRef<any>;
  html5QrCode!: Html5Qrcode;

  checkPointForm: UntypedFormGroup = new UntypedFormGroup({
    qr_text: new UntypedFormControl('', Validators.required),
    name: new UntypedFormControl('', [
      Validators.required,
      Validators.maxLength(100),
    ]),
    company: new UntypedFormControl('', Validators.required),
    description: new UntypedFormControl(''),
    is_locked: new UntypedFormControl(true),
    company_id: new UntypedFormControl('', Validators.required),
    site: new UntypedFormControl(''),
    site_id: new UntypedFormControl(''),

    min_distance: new UntypedFormControl(100, [
      Validators.max(1000),
      Validators.required,
    ]),

    address1: new UntypedFormControl(''),
    address2: new UntypedFormControl(),
    address3: new UntypedFormControl(),
    city_name: new UntypedFormControl(''),
    state_code: new UntypedFormControl(''),
    state_name: new UntypedFormControl(''),
    country_name: new UntypedFormControl(''),
    postcode: new UntypedFormControl(''),
    longitude: new UntypedFormControl(null),
    latitude: new UntypedFormControl(null),
  });

  checkpointFormBackup = JSON.parse(JSON.stringify(this.checkPointForm.value));

  registerScan: boolean = true;
  scanInProgress: boolean = false;

  isAdmin: boolean = false;
  isDispatchUser: boolean = false;

  showAddCheckpointForm: boolean = false;
  cameraDevices: any = [];

  isTorchAvailable: boolean = false;
  devieTorchOn: boolean = false;

  // values passed from the job to save to model
  patrolRouteId: string = '';
  jobId: string = '';

  offlineMode = !navigator.onLine;

  cachedQRCheckpoints: any;

  cameraFeedDetected: boolean = true;

  userLocation: any = {};

  currentStep: number = 1;

  selectedCameraIndex: any;

  videoElementHidden: boolean = false;

  searchSiteValue: any;
  siteListData: any = [];
  initialSiteDataList: any = [];

  cameraConfig = {
    fps: 29,
    qrbox: { width: 250, height: 250 },
    rememberLastUsedCamera: true,
    showTorchButtonIfSupported: true,
    torch: true,
    advanced: [{ torch: true }],
  };

  zoomValuesAvailable: any = [];
  showZoomRange: any = [1, 3, 5, 10];
  selectedZoom: number = 1;
  continuousQRScan: boolean = false;
  selectedDeviceId: string = '';

  // nfc reader params
  nfcReader: boolean = false;
  nfcErrorOutput: any;

  isIphone: boolean = this.deviceInfoService.isPhoneIOS();
  isIphonePwa: boolean = this.isIphone && this.deviceInfoService.isDevicePWA();
  iphoneUltraWideCam: any;

  deviceInfoSubscription: any;

  lastScanResponse: any;

  scanTimer: any = {};
  scanTimerLogs: any = [];

  ndefReader: any;
  ndefReadingHandler: any;
  ndefReadingErrorHandler: any;

  isNativeApp = Capacitor.isNativePlatform();
  nativeCameraCapabilities: any = {};

  nativeAppQRScanListener: any;
  nativeAppNFCScanListener: any;
  nativeAppNFCScanStopListner: any;

  nativeAppCameraPermission = { permissionDenied: false, otherError: false };

  constructor(
    @Optional() protected dialogRef: DialogRef,
    private dialogService: ModalDialogueService,
    private jobService: JobsService,
    private checkpointService: CheckpointService,
    private spinnerService: LoadingSpinnerService,
    private dataCheckService: DataCheckService,
    private toasterService: ToasterService,
    @Inject(DIALOG_DATA) public dialogueData: any,
    private swPush: SwPush,
    private pageService: PagesService,
    private cdr: ChangeDetectorRef,
    private deviceInfoService: DeviceInfoService,
    private clientService: ClientsService,
    private appService: AppService,
    private viewContainerRef: ViewContainerRef,
    private sqliteDb: SqliteDbService,
  ) {}

  ngOnInit(): void {
    this.isAdmin = this.dataCheckService.isUserAdmin();
    this.isDispatchUser = this.dataCheckService.isDispatchUser();
    if (this.dialogueData) {
      this.patrolRouteId = this.dialogueData?.patrolRouteId || '';
      this.jobId = this.dialogueData?.jobId || '';
      this.nfcReader = this.dialogueData?.nfcReader || false;
    }

    this.deviceInfoSubscription =
      this.deviceInfoService.deviceInfoSubject.subscribe((value: any) => {
        if ('appStatus' in value) {
          this.offlineMode = !value?.appStatus;
          this.siteListData = [...this.siteListData];

          if (this.offlineMode) {
            this.checkPointForm.controls['is_locked'].setValue(false);
            if (this.currentStep === 2) {
              this.backPreviousStep();
            }
          }
          this.cdr.detectChanges();
        }
      });
    const storedCameraIndex = sessionStorage.getItem('selectedCameraIndex');
    if (storedCameraIndex) {
      this.selectedCameraIndex = parseInt(storedCameraIndex, 0);
    }
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }
  visibilityHandler = () => {
    if (document.hidden) {
      this.onCloseDialogue();
    }
  };
  toggleCollapse() {
    var myCollapse = document.getElementById('collapseId');
    var collapse = new bootstrap.Collapse(myCollapse, {
      toggle: false,
    });
    collapse.toggle();
  }

  async ngAfterViewInit(): Promise<void> {
    this.pageService.miscSubjectParam.next({
      isOnEvent: true,
    });

    this.cdr.detectChanges();

    this.scanTimer['called'] = performance.now();

    if (this.nfcReader) {
      this.deviceInfoService
        .getGpsCoordinates()
        .then((gps: any) => (this.userLocation = gps));
      this.scanTimer['start'] = performance.now();

      // open nfc scan other wise qr scan
      if (this.isNativeApp) {
        this.readNFCviaNative();
      } else {
        this.readNFCWeb();
      }
    } else {
      if (this.isNativeApp) {
        this.startNativeQRScanner();
      } else {
        this.scanTimer['start'] = performance.now();
        // https://app.shortcut.com/sendem/story/10868/ios-safari-camera-prompts

        const isDevicePWA = this.deviceInfoService.isDevicePWA();

        const currentDeviceInfo: any =
          await this.deviceInfoService.getDeviceInfo();
        const iosSafariBrowser =
          currentDeviceInfo?.browser == 'Safari' &&
          !isDevicePWA &&
          this.isIphone;

        const safariPermissionSettingsUpdated =
          localStorage.getItem(btoa('safariPermissionSettingsUpdated')) ===
          'true';
        if (iosSafariBrowser && !safariPermissionSettingsUpdated) {
          const userGuide = JSON.parse(
            localStorage.getItem('userGuide') || '{}',
          );

          if (!userGuide?.hide_safari_access_prompts) {
            const splashDialogRef = this.dialogService.open(
              SplashScreenComponent,
              {
                data: { cameraPromptCheck: true },
              },
            );

            const shouldShowGuide = await firstValueFrom(
              splashDialogRef.afterClosed(),
            );

            if (shouldShowGuide) {
              const guideDialogRef = this.dialogService.open(
                ScreenshotGuideComponent,
                {
                  data: { guide: 'safariPermissionPrompts' },
                },
              );

              await firstValueFrom(guideDialogRef.afterClosed());
            }
          }
        }

        this.html5QRReader();
      }
    }

    if (!this.cachedQRCheckpoints?.length) {
      this.cachedQRCheckpoints =
        await this.checkpointService.getQRCheckpointList();
    }
  }
  async startNativeQRScanner() {
    this.closeQRListeners();
    const qrScanner: any = document.getElementById('qr-scanner');
    const rect = qrScanner.getBoundingClientRect();
    this.spinnerService.hide();

    try {
      await QRScanner.start({
        frame: {
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        },

        autoZoom: true, // only in ios
        torch: false,
        defaultZoom: 1,
      });
    } catch (error: any) {
      this.nativeAppCameraPermission = {
        permissionDenied: false,
        otherError: false,
      };
      if (error?.code == 'CAMERA_PERMISSION_DENIED') {
        this.nativeAppCameraPermission = {
          permissionDenied: true,
          otherError: false,
        };
      } else {
        this.nativeAppCameraPermission = {
          permissionDenied: false,
          otherError: true,
        };
      }
      this.cdr.detectChanges();
      return;
    }

    await this.sleep(500);

    this.nativeCameraCapabilities = await QRScanner.getCapabilities();
    if (this.selectedCameraIndex) {
      let deviceID =
        this.nativeCameraCapabilities?.cameras?.[this.selectedCameraIndex]
          ?.id || '';

      if (deviceID) {
        await QRScanner.setCamera({
          id: deviceID,
        });
      }
    }

    this.isTorchAvailable = this.nativeCameraCapabilities?.hasTorch;

    const availableValues = Array.from(
      { length: this.nativeCameraCapabilities?.maxZoom - 1 + 1 },
      (_, i) => i + 1,
    );
    this.zoomValuesAvailable = availableValues?.filter((value: any) =>
      this.showZoomRange?.includes(value),
    );
    this.cdr.detectChanges();

    this.nativeAppQRScanListener = QRScanner.addListener(
      'scan',
      async (result: any) => {
        this.isTorchAvailable = false;
        await QRScanner?.pause();
        this.onReadingQRNFC(result?.value);
      },
    );
  }

  async onCloseDialogue() {
    try {
      if ([2, 3].includes(this.html5QrCode?.getState())) {
        this.html5QrCode?.stop();
      }
      await QRScanner?.stop();
    } catch (error) {}

    this.closeDialogBox(this.lastScanResponse ? true : false);
  }
  closeDialogBox(val: any = false) {
    // if (this.scanTimerLogs?.length) {
    //   let scanTimerLogList: any = JSON.parse(
    //     localStorage.getItem(btoa('scanTimerLogs')) || '[]',
    //   );
    //   scanTimerLogList = [...scanTimerLogList, ...this.scanTimerLogs];
    //   localStorage.setItem(
    //     btoa('scanTimerLogs'),
    //     JSON.stringify(scanTimerLogList),
    //   );
    // }
    this.spinnerService.hide();
    this.dialogRef.close(val);
  }
  async ngOnDestroy() {
    try {
      if ([2, 3].includes(this.html5QrCode?.getState())) {
        this.html5QrCode?.stop();
      }
      await QRScanner?.stop();
    } catch (error) {}

    this.pageService.miscSubjectParam.next({
      isOnEvent: false,
    });
    if (this.deviceInfoSubscription) this.deviceInfoSubscription?.unsubscribe();

    document.removeEventListener('visibilitychange', this.visibilityHandler);

    this.closeNFCListeners();
    this.closeQRListeners();
  }
  async closeQRListeners() {
    if (this.nativeAppQRScanListener) {
      await this.nativeAppQRScanListener?.remove();
      this.nativeAppQRScanListener = null;
    }
  }

  async closeNFCListeners() {
    if (this.isNativeApp) {
      if (this.nativeAppNFCScanListener) {
        await this.nativeAppNFCScanListener?.remove();
        this.nativeAppNFCScanListener = null;
      }

      if (this.nativeAppNFCScanStopListner) {
        await this.nativeAppNFCScanStopListner?.remove();
        this.nativeAppNFCScanStopListner = null;
      }
    } else {
      if (this.ndefReader) {
        if (this.ndefReadingHandler)
          this.ndefReader.removeEventListener(
            'reading',
            this.ndefReadingHandler,
          );
        if (this.ndefReadingErrorHandler)
          this.ndefReader.removeEventListener(
            'readingerror',
            this.ndefReadingErrorHandler,
          );
      }
    }
  }
  sleep(ms = 1000) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  html5QRReader() {
    this.spinnerService.show('Opening Camera');
    this.html5QrCode = new Html5Qrcode('html5-qr-reader', {
      verbose: false,
      useBarCodeDetectorIfSupported: true,
    });

    Html5Qrcode.getCameras() // Call the static method on the class
      .then((devices: any) => {
        setTimeout(() => {
          this.cameraDevices = devices;

          this.deviceInfoService
            .getGpsCoordinates()
            .then((gps: any) => (this.userLocation = gps));

          if (devices && devices.length) {
            let devcieID = devices[devices.length - 1]?.id;
            if (
              this.selectedCameraIndex >= 0 &&
              this.selectedCameraIndex < devices?.length
            ) {
              devcieID = devices[this.selectedCameraIndex]?.id;
            } else {
              const backCameras = this.cameraDevices?.filter(
                (device: any) =>
                  device.facingMode === 'environment' ||
                  device.label.toLowerCase().includes('back'),
              );

              if (this.isIphone) {
                this.iphoneUltraWideCam = backCameras?.find((cam: any) =>
                  cam?.label?.toLowerCase().includes('ultra wide'),
                );
              }
              if (this.iphoneUltraWideCam?.id) {
                devcieID = this.iphoneUltraWideCam?.id;
              } else {
                if (backCameras?.length) {
                  devcieID = backCameras[backCameras?.length - 1]?.id;
                }
              }
            }

            this.startCamera(devcieID);
          }
          // setTimeout is for firefox as it is not able to get devices fast
        }, 500);
      })
      .catch((err) => {
        this.checkCameraFeed();
        this.spinnerService.hide();
      });
  }
  checkCameraFeed() {
    try {
      const videoElement = document.querySelector('video');

      if (videoElement) {
        videoElement.addEventListener('playing', () => {
          setTimeout(() => {
            // this is for firefox as height is 0 without a timeout even if the feed is there

            this.cameraFeedDetected = isVideoPlaying(videoElement);

            if (this.cameraFeedDetected) {
              this.isTorchSupported(this.html5QrCode);
              this.isZoomSupported(this.html5QrCode);
            }
            this.spinnerService.hide();
          }, 500);
        });
        setTimeout(() => {
          // this is for iphones as some devices are not listening to 'playing' eventListner
          if (this.videoElementHidden === false) {
            this.spinnerService.hide();
          }

          this.cameraFeedDetected = isVideoPlaying(videoElement);
          if (this.cameraFeedDetected) {
            this.isTorchSupported(this.html5QrCode);
            this.isZoomSupported(this.html5QrCode);
          }
          this.spinnerService.hide();
        }, 1000);
      } else {
        this.spinnerService.hide();

        setTimeout(() => {
          if (this.videoElementHidden === false) {
            this.cameraFeedDetected = false;
            this.cdr.detectChanges();
          }
        }, 500);
      }

      function isVideoPlaying(video: any) {
        return (
          video.currentTime > 0 &&
          !video.paused &&
          !video.ended &&
          video.readyState > 2 &&
          video.videoWidth > 0 &&
          video.videoHeight > 0
        );
      }
    } catch (error) {
      this.spinnerService.hide();
      this.cameraFeedDetected = false;
    }
  }
  checkAndCallAfterFuntions() {
    if (this.html5QrCode.getState() === 2) {
      this.spinnerService.show('Configuring Settings & Feeds');
      this.checkCameraFeed();
    } else {
      setTimeout(() => {
        this.checkAndCallAfterFuntions();
      }, 500);
    }
  }
  isZoomSupported(html5Qrcode: Html5Qrcode) {
    let settings: any = html5Qrcode.getRunningTrackCapabilities();
    if ('zoom' in settings) {
      const availableValues = Array.from(
        { length: settings?.zoom?.max - settings?.zoom?.min + 1 },
        (_, i) => i + settings?.zoom?.min,
      );
      this.zoomValuesAvailable = availableValues?.filter((value: any) =>
        this.showZoomRange?.includes(value),
      );
      if (this.zoomValuesAvailable?.length) {
        let selectedZoomRange = this.zoomValuesAvailable[0];

        if (
          typeof this.selectedCameraIndex !== 'number' &&
          this.iphoneUltraWideCam?.id &&
          this.zoomValuesAvailable?.includes(3)
        ) {
          selectedZoomRange = 3;
        }

        this.applyZoom(selectedZoomRange);
      }
    }
    this.cdr.detectChanges();
  }
  async applyZoom(zoomValue: number) {
    try {
      this.selectedZoom = zoomValue;
      if (this.isNativeApp) {
        await QRScanner.setZoom({
          factor: zoomValue,
        });
      } else {
        let constraints: any = {
          advanced: [{ zoom: zoomValue }],
        };

        await this.html5QrCode?.applyVideoConstraints(constraints);
      }
    } catch (error) {
      throw error;
    } finally {
      this.cdr.detectChanges();
    }
  }
  waitForTorchBtn = (
    maxRetries = 3,
    delay = 100,
  ): Promise<HTMLElement | false> => {
    return new Promise((resolve) => {
      let attempts = 0;

      const check = () => {
        const btn = document.getElementById('torchBtn');
        if (btn) {
          resolve(btn as HTMLElement);
        } else {
          attempts++;
          if (attempts >= maxRetries) {
            resolve(false);
          } else {
            setTimeout(check, delay);
          }
        }
      };

      check();
    });
  };

  async isTorchSupported(html5Qrcode: Html5Qrcode) {
    let settings = html5Qrcode.getRunningTrackSettings();
    this.isTorchAvailable = 'torch' in settings;

    this.cdr.detectChanges();

    if (this.isTorchAvailable) {
      const lastSavedTorchStatus =
        localStorage.getItem(btoa('lastSavedTorchStatus')) || 'on';

      if (lastSavedTorchStatus === 'on') {
        const torchBtn = await this.waitForTorchBtn();
        if (torchBtn) {
          this.turnOffOnTorchHTML5('on');
        }
      }
    }
  }
  async turnOffOnTorchHTML5(action: string, isRegistering = false) {
    if (action == 'on') {
      let constraints: any = {
        torch: true,
        advanced: [{ torch: true }],
      };
      await this.html5QrCode?.applyVideoConstraints(constraints);
      let settings: any = this.html5QrCode?.getRunningTrackSettings();

      if (Boolean(/iPad|iPhone|iPod/.test(navigator.userAgent))) {
        this.devieTorchOn = true;
      } else {
        // Because this part doesn't work on iPhone for some reason...
        if (settings?.torch === true) {
          this.devieTorchOn = true;
        } else {
          this.devieTorchOn = false;
        }
      }

      this.cdr.detectChanges();
    } else {
      let constraints: any = {
        torch: false,
        advanced: [{ torch: false }],
      };
      this.devieTorchOn = false;
      await this.html5QrCode?.applyVideoConstraints(constraints);
    }
    if (!isRegistering)
      localStorage.setItem(btoa('lastSavedTorchStatus'), action);
  }
  async getCheckpointFromIndexedDB() {
    return this.cachedQRCheckpoints?.find(
      (qrCp: any) =>
        qrCp?.qr_code === this.checkPointForm.value?.qr_text &&
        (this.nfcReader
          ? qrCp?.cp_type == 2
          : qrCp?.cp_type == 1 || !qrCp?.cp_type),
    );
  }
  getCheckpointFromServer() {
    return new Promise((resolve) => {
      this.checkpointService
        .listCheckpointData({
          qr_lookup: 1,
          qr_text: this.checkPointForm.value?.qr_text,
          checkpoint_type: this.nfcReader ? 2 : 1,
        })
        .subscribe(async (response: any) => {
          if (response?.status == 'failure' && response?.id) {
            resolve(response);
          } else {
            resolve({});
          }
        });
    });
  }
  async onQRNFCLookup() {
    let scannedQRNFCData: any = {};
    this.scanTimer['ui_lookup_start'] = performance.now();
    scannedQRNFCData = await this.getCheckpointFromIndexedDB();
    this.scanTimer['ui_lookup_end'] = performance.now();
    if (!scannedQRNFCData?.id) {
      this.scanTimer['server_lookup_start'] = performance.now();
      scannedQRNFCData = await this.getCheckpointFromServer();
      this.scanTimer['server_lookup_end'] = performance.now();
    }
    if (scannedQRNFCData?.id) {
      let data: any = {
        qr_text: this.checkPointForm.value?.qr_text,
        check_point_id: scannedQRNFCData?.id,
        user_prompts: scannedQRNFCData?.user_prompts,
        user_prompt_count:
          scannedQRNFCData?.user_prompt_count ||
          scannedQRNFCData?.user_prompts?.filter((item: any) => item?.id)
            ?.length,
        minDistance: scannedQRNFCData?.min_distance,
        gps: scannedQRNFCData?.gps,
        checkpointDetail: {
          name: scannedQRNFCData?.name,
          company: scannedQRNFCData?.company,
          site_name: scannedQRNFCData?.site_name,
          cp_type: scannedQRNFCData?.cp_type,
        },
      };
      if (this.patrolRouteId) {
        data.patrol_route_id = this.patrolRouteId;
      }
      if (this.jobId) {
        data.job_id = this.jobId;
      }

      this.openUserPrompt(data);
    } else {
      this.registerQRCode();
    }
  }

  async registerQRCode() {
    this.spinnerService.hide();

    const qrInQueue: any = await this.checkQrInQueue(
      this.checkPointForm.value?.qr_text,
    );

    if (qrInQueue) {
      const formData = qrInQueue?.eventId
        ? JSON.parse(JSON.parse(qrInQueue?.data)?.body?.form_data)
        : JSON.parse(qrInQueue?.form_data);

      let body: any = {
        ...(formData || {}),
        scan_after_register: true,
        qr_text: this.checkPointForm.value?.qr_text,
        scanOnly: true,
      };

      if (this.nfcReader) body.nfc_tag = true;
      let reqBody = { form_data: JSON.stringify(body) };

      this.callRegisterQRNFCApi(reqBody, 'resume');
    } else {
      if (this.isNativeApp) await QRScanner?.stop();

      if (!this.offlineMode) {
        this.jobService.getAllSiteList().subscribe((response: any) => {});
      }
      if (this.devieTorchOn) this.turnOffOnTorchHTML5('off', true);
      const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
        data: {
          title: `New ${this.nfcReader ? 'NFC Tag' : 'QR Code'} Scanned`,
          message: `Tap confirm to add as a checkpoint.`,
        },
      });
      dialogRef.afterClosed().subscribe((value: any) => {
        if (value === true) {
          this.showAddCheckpointForm = true;
          this.showAddressLookupForm('forward');
          this.currentStep = 1;
          this.initializeToolTip();

          this.onSiteSearch();
          this.cdr.detectChanges();
        } else {
          this.onCloseDialogue();
        }
      });
    }
  }
  async checkGeoFence(checkpointData: any) {
    return new Promise(async (resolve) => {
      if (checkpointData?.minDistance && checkpointData?.gps) {
        if (!this.userLocation?.lat) {
          this.userLocation = await this.deviceInfoService.getGpsCoordinates();
        }

        resolve(
          showGeoFenceAlert(
            checkpointData?.minDistance,
            checkpointData?.gps?.lat,
            this.userLocation?.lat,
            checkpointData?.gps?.lon,
            this.userLocation?.lon,
          ),
        );
      } else {
        resolve(false);
      }
    });
  }
  async scanQRCheckpoint(checkpointData: any) {
    this.spinnerService.show('Geofence check');
    this.scanTimer['geo_fence_start'] = performance.now();
    const geoFenceRadiusAlert: any = await this.checkGeoFence(checkpointData);
    this.scanTimer['geo_fence_end'] = performance.now();
    if (geoFenceRadiusAlert) {
      this.spinnerService.hide();
      const checkpointGps = {
        scan_detail: checkpointData?.gps,
        min_distance: checkpointData?.minDistance,
        name: checkpointData?.checkpointDetail?.name,
        //passing tooltip value to show in the map marker
        gps: { markerTooltip: 'Your Location', ...this.userLocation },
      };

      const dialogRef = this.dialogService.open(
        this.qrScanWarningTemplate,
        {
          data: checkpointGps,
        },
        this.viewContainerRef,
      );
      dialogRef.afterClosed().subscribe((value: any) => {
        if (value === true) {
          this.onScanQRNFC(checkpointData);
        } else {
          this.onCloseDialogue();
        }
      });
    } else {
      this.spinnerService.hide();
      this.onScanQRNFC(checkpointData);
    }
  }

  async onScanQRNFC(body: any) {
    if (this.scanInProgress) return;

    this.scanInProgress = true;

    this.scanTimer['upload_scan_start'] = performance.now();

    let fileData: FormData = new FormData();
    Object.keys(body).forEach((key) => {
      fileData.append(key, body[key]);
    });
    this.spinnerService.show('Uploading Scan');
    const response: any = await this.appService.backUpAndUploadUpdate(
      `patrol/scan_checkpoint`,
      fileData,
      {},
      'POST',
      this.userLocation,
    );

    this.scanTimer['upload_scan_end'] = performance.now();

    this.scanTimer['is_nfc'] = this.nfcReader || false;

    this.scanTimerLogs.push({
      recorded_at: new Date().getTime() / 1000,
      additional_info: this.scanTimer,
    });

    if (response['status'] === 'success') {
      this.spinnerService.hide();
      if (!this.continuousQRScan) {
        this.spinnerService.hide();
        setTimeout(() => {
          this.toasterService.setMessage({ successTick: true });
        }, 200);

        this.closeDialogBox(response);
      } else {
        this.lastScanResponse = true;
        this.scanTimer = {
          start: performance.now(),
          called: performance.now(),
        };
        if (this.isNativeApp) {
          this.toasterService.setMessage({ successTick: 'isNativeApp' });
        } else {
          this.toasterService.setMessage({ successTick: true });
          this.spinnerService.show('Resuming Scan');
        }

        this.restartQRCodeScan();
      }
    } else {
      this.spinnerService.hide();
      if (!this.continuousQRScan) {
        this.closeDialogBox(true);
        this.toasterService.setMessage({ failureCross: true });
      } else {
        if (this.isNativeApp) {
          this.toasterService.setMessage({ failureCross: 'isNativeApp' });
        } else {
          this.toasterService.setMessage({ failureCross: true });
          this.spinnerService.show('Resuming Scan');
        }
        this.restartQRCodeScan();
      }
    }
  }
  async restartQRCodeScan(action: 'restart' | 'resume' = 'resume') {
    this.scanInProgress = false;
    this.showAddCheckpointForm = false;
    this.checkPointForm.patchValue(this.checkpointFormBackup);
    this.checkPointForm.controls['qr_text'].setValue(null);

    if (this.isNativeApp) {
      if (action == 'restart') {
        setTimeout(() => {
          this.startNativeQRScanner();
        }, 1000);
      } else {
        setTimeout(async () => {
          this.spinnerService.hide();
          await QRScanner?.resume();
        }, 1000);
      }
    } else {
      if (action == 'restart') {
        this.html5QrCode?.stop();
        setTimeout(() => {
          this.spinnerService.hide();
          this.startCamera(this.selectedDeviceId);
        }, 1000);
      } else {
        setTimeout(() => {
          this.html5QrCode.resume();
          this.spinnerService.hide();
          if (this.cameraFeedDetected) {
            this.isTorchSupported(this.html5QrCode);
          }
        }, 1000);
      }
    }
  }
  getSortedPromtList(arrayList: any) {
    return sortArrayOfObject(arrayList, 'order', 'desc');
  }
  openUserPrompt(checkPointData: any) {
    if (checkPointData?.user_prompt_count < 1) {
      this.scanQRCheckpoint(checkPointData);
      return;
    }

    this.scanTimer['prompt_menu'] = performance.now();
    let promptResults: any = [];
    let counter = 0;
    const dialogRefs: any = [];
    this.getSortedPromtList(checkPointData?.user_prompts)?.forEach(
      async (prompt: any) => {
        const dialogref = this.dialogService.open(PrompterComponent, {
          data: { promptData: prompt?.prompts },
        });
        dialogRefs.push(dialogref);
        const value = await firstValueFrom(dialogref.afterClosed());
        if (value) {
          counter += 1;
          if (value?.length) {
            promptResults.push({
              data: value,
              prompt_data: { id: prompt?.id, name: prompt?.name },
            });
          }
          if (counter === checkPointData?.user_prompt_count) {
            checkPointData.prompt_data = JSON.stringify(promptResults);
            this.scanQRCheckpoint(checkPointData);
          }
        } else {
          if (value === false) {
            dialogRefs?.forEach((dialogRef: any) => dialogRef?.close());
            this.toasterService.setMessage({
              successMessage: '',
              errorMessage: 'Checkpoint was not scanned',
            });
            this.onCloseDialogue();
          }
        }
      },
    );
  }
  async callRegisterQRNFCApi(
    reqBody: any,
    continueAction: 'restart' | 'resume' = 'resume',
  ) {
    let fileData: FormData = new FormData();
    Object.keys(reqBody).forEach((key) => {
      fileData.append(key, reqBody[key]);
    });
    this.spinnerService.show('Adding new Checkpoint');
    const response: any = await this.appService.backUpAndUploadUpdate(
      `check_point/register_qr`,
      fileData,
      {},
      'POST',
      this.userLocation,
    );

    if (response?.status == 'success') {
      this.spinnerService.hide();
      if (!this.continuousQRScan) {
        this.closeDialogBox(true);
      } else {
        this.lastScanResponse = true;
        this.spinnerService.show('Resuming Scan');
        this.restartQRCodeScan(continueAction);
      }
    } else {
      this.spinnerService.hide();
      if (!this.continuousQRScan) {
        this.closeDialogBox();
      } else {
        this.spinnerService.show('Resuming Scan');
        this.restartQRCodeScan(continueAction);
      }

      this.toasterService.setMessage({
        successMessage: '',
        errorMessage: response?.message,
      });
    }
  }
  registerQRNFCCheckpoint(body: any) {
    if (this.registerScan) {
      body.scan_after_register = true;
      if (this.patrolRouteId) {
        body.patrol_route_id = this.patrolRouteId;
      }
      if (this.jobId) {
        body.job_id = this.jobId;
      }
    }
    if (this.nfcReader) body.nfc_tag = true;
    let reqBody = { form_data: JSON.stringify(body) };

    this.callRegisterQRNFCApi(reqBody, 'restart');
  }

  onClickSubmit() {
    let body: any = this.checkPointForm.value;
    if (body.site_id == null || body.site_id == undefined) {
      delete body.site_id;
    }
    if (body) {
      this.registerQRNFCCheckpoint(body);
    }
  }
  shiftFocus(elementId: string, time?: number) {
    setTimeout(() => {
      var element = <HTMLInputElement>document.getElementById(elementId);
      element?.focus();
    }, time);
  }

  onReadingQRNFC(readValue: string) {
    this.scanTimer['value_read'] = performance.now();

    this.checkPointForm.controls['qr_text'].setValue(readValue);
    this.onQRNFCLookup();
  }
  showMapMethod() {
    this.spinnerService.show();
    this.checkPointForm.controls['address1'].setValue('');
    this.checkPointForm.controls['address2'].setValue('');
    this.checkPointForm.controls['address3'].setValue('');
    this.checkPointForm.controls['city_name'].setValue('');
    this.checkPointForm.controls['postcode'].setValue('');
    this.checkPointForm.controls['state_code'].setValue('');
    this.checkPointForm.controls['state_name'].setValue('');

    setTimeout(async () => {
      if (!this.userLocation?.lat) {
        this.userLocation = await this.deviceInfoService.getGpsCoordinates();
      }

      this.checkPointForm.controls['latitude'].setValue(this.userLocation?.lat);
      this.checkPointForm.controls['longitude'].setValue(
        this.userLocation?.lon,
      );
      this.cdr.detectChanges();
      this.spinnerService.hide();
    }, 100);
  }
  backPreviousStep() {
    this.checkPointForm.controls['latitude'].setValue(null);
    this.checkPointForm.controls['longitude'].setValue(null);
    this.checkPointForm.controls['is_locked'].setValue(false);
    this.checkPointForm.controls['longitude'].removeValidators(
      Validators.required,
    );
    this.checkPointForm.controls['latitude'].removeValidators(
      Validators.required,
    );
  }
  showHideAddressFields(event: any) {
    const selected = event.target.checked;
    this.checkPointForm.controls['is_locked'].setValue(selected);
    if (selected) {
      this.checkPointForm.controls['longitude'].setValidators(
        Validators.required,
      );
      this.checkPointForm.controls['latitude'].setValidators(
        Validators.required,
      );
    } else {
      this.backPreviousStep();
    }
    this.showAddressLookupForm(selected ? 'forward' : 'backward');
  }

  addressSelected(address: any) {
    if (address && typeof address === 'object') {
      this.checkPointForm.controls['address1'].setValue(address?.address1);
      this.checkPointForm.controls['address2'].setValue(address?.address2);
      this.checkPointForm.controls['address3'].setValue(address?.address3);
      this.checkPointForm.controls['city_name'].setValue(address?.city_name);
      this.checkPointForm.controls['postcode'].setValue(address?.postcode);
      this.checkPointForm.controls['state_code'].setValue(address?.state_code);
      this.checkPointForm.controls['state_name'].setValue(
        address?.state_name || address?.state_code,
      );
      this.checkPointForm.controls['country_name'].setValue(
        address?.country_name || getInstanceRegion(),
      );
      this.checkPointForm.controls['latitude'].setValue(address?.latitude);
      this.checkPointForm.controls['longitude'].setValue(address?.longitude);
    }
  }

  emitData(event: any) {
    this.checkPointForm.controls['latitude'].setValue(event.lat);
    this.checkPointForm.controls['longitude'].setValue(event.lng);
  }
  updateMinDistance(event: any) {
    // method to update the min distance emitted from the map component
    this.checkPointForm.controls['min_distance'].setValue(event);
  }

  async checkQrInQueue(qrValue: string) {
    try {
      if (this.isNativeApp) {
        const result = await this.sqliteDb.query(
          `SELECT * FROM saved_updates WHERE type = ?`,
          ['register_qr'],
        );

        return result?.find(
          (item: any) =>
            JSON.parse(JSON.parse(item?.data)?.body?.form_data)?.qr_text ===
            qrValue,
        );
      } else if (this.swPush.isEnabled) {
        const result = await gTDB.gtSavedData.toArray();

        return result?.find(
          (item: any) =>
            item?.pathName === '/api/check_point/register_qr/' &&
            JSON.parse(item?.form_data)?.qr_text === qrValue,
        );
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }
  startCamera(deviceID: any) {
    this.selectedDeviceId = deviceID;
    this.html5QrCode
      .start(
        deviceID,
        this.cameraConfig,
        (decodedText, decodedResult) => {
          this.isTorchAvailable = false;
          this.onReadingQRNFC(decodedText);
          this.html5QrCode.pause();
        },
        (errorMessage) => {
          // parse error, ignore it.
        },
      )
      .then(() => {
        setTimeout(() => {
          this.checkAndCallAfterFuntions();
          this.videoElementHidden = false;
        }, 1);
      })
      .catch((error) => {
        this.spinnerService.hide();
        this.cameraFeedDetected = false;
        this.videoElementHidden = false;
        this.cdr.detectChanges();

        throw error;
      });
  }
  initializeToolTip() {
    this.appService.initializeBootstrapTooltips();
  }

  showAddressLookupForm(action: string) {
    setTimeout(() => {
      if (
        (this.isAdmin || this.isDispatchUser) &&
        this.checkPointForm.value.is_locked &&
        action == 'forward'
      ) {
        this.checkPointForm.controls['city_name'].setValidators(
          Validators.required,
        );
      } else {
        this.checkPointForm.controls['city_name'].removeValidators(
          Validators.required,
        );
      }
      this.checkPointForm.updateValueAndValidity();
    }, 100);
  }

  async cycleCamera() {
    if (this.isNativeApp) {
      this.selectedCameraIndex =
        ((this.selectedCameraIndex || 0) + 1) %
        this.nativeCameraCapabilities.cameras.length;
      sessionStorage.setItem(
        'selectedCameraIndex',
        this.selectedCameraIndex.toString(),
      );
      let deviceID =
        this.nativeCameraCapabilities.cameras[this.selectedCameraIndex]?.id;
      await QRScanner.setCamera({
        id: deviceID,
      });
      return;
    }
    if (this.cameraDevices.length > 1) {
      this.videoElementHidden = true;
      this.spinnerService.show();
      this.selectedCameraIndex =
        ((this.selectedCameraIndex || 0) + 1) % this.cameraDevices.length;
      sessionStorage.setItem(
        'selectedCameraIndex',
        this.selectedCameraIndex.toString(),
      );
      let deviceID = this.cameraDevices[this.selectedCameraIndex]?.id;

      if (this.html5QrCode.getState() == 2) {
        this.html5QrCode.stop().then(() => {
          setTimeout(() => {
            this.startCamera(deviceID);
          }, 100);
        });
      } else {
        // already stopped
        setTimeout(() => {
          this.startCamera(deviceID);
        }, 100);
      }
    }
  }
  onSiteSearch(event?: any) {
    const searchString = event ? event.target.value : '';
    this.clientService
      .searchSites({
        search_str: searchString,
        is_active: 1,
      })
      .subscribe((res: any) => {
        if (res['status'] == 'success') {
          this.siteListData = res?.data;
          this.sortSiteListData();
        }
        if (!searchString) {
          this.initialSiteDataList = JSON.parse(
            JSON.stringify(this.siteListData),
          );
        }
      });
  }

  async sortSiteListData() {
    if (!this.userLocation?.lat) {
      this.userLocation = await this.deviceInfoService.getGpsCoordinates();
    }
    this.siteListData?.forEach((item: any) => {
      item.hDistance = Number(
        getDistanceUsingHaversine(
          this.userLocation?.lat,
          item?.gps?.lat,
          this.userLocation?.lon,
          item?.gps?.lon,
        ),
      );
    });
    await Promise.all(
      this.siteListData?.map((item: any) => {
        const distance =
          getDistanceUsingHaversine(
            this.userLocation?.lat,
            item?.gps?.lat,
            this.userLocation?.lon,
            item?.gps?.lon,
          ) || 0;
        item.hDistance = Number(distance);
      }),
    );

    this.siteListData = [
      ...sortArrayOfObject(this.siteListData, 'hDistance', 'asc'),
    ]?.splice(0, 10);

    this.cdr.detectChanges();
  }
  onSiteSelect(data: any) {
    if (data?.key === 0 && !this.offlineMode) {
      this.openAddSiteForm();
      this.checkPointForm.controls['site_id'].setValue(null);
      this.checkPointForm.controls['site'].setValue(null);
    }
    if (data?.id) {
      this.checkPointForm.controls['site_id'].setValue(data.id);
      this.checkPointForm.controls['site'].setValue(data.company_name);

      if (data?.company) {
        this.checkPointForm.controls['company_id'].setValue(data?.company?.id);
        this.checkPointForm.controls['company'].setValue(
          data?.company?.company_name,
        );
      }
    }
  }
  openAddSiteForm() {
    const dialogRef = this.dialogService.open(AddEditSiteComponent, {
      data: {
        detailData: { company_name: this.searchSiteValue },
      },
    });
    dialogRef.afterClosed().subscribe((data: any) => {
      if (data !== 'close') {
        this.onSiteSelect(data);
      }
    });
  }
  editField(field: string) {
    if (field == 'name') {
      this.checkPointForm.controls['name'].setValue(null);
      this.shiftFocus('cpName');
    } else if (field == 'site') {
      this.checkPointForm.controls['site_id'].setValue(null);
      this.checkPointForm.controls['site'].setValue(null);
      this.checkPointForm.controls['company_id'].setValue(null);
      this.checkPointForm.controls['company'].setValue(null);
      this.siteListData = JSON.parse(JSON.stringify(this.initialSiteDataList));
      this.sortSiteListData();
      this.shiftFocus('siteId');
    }
  }
  async readNFCviaNative() {
    this.closeNFCListeners();
    try {
      this.nativeAppNFCScanListener = await CapacitorNfc.addListener(
        'nfcEvent',
        async (event: any) => {
          const serial =
            event?.tag?.id ||
            event?.tag?.identifier ||
            event?.id ||
            event?.serialNumber;

          const uidHex = serial
            .map((b: number) => b.toString(16).padStart(2, '0'))
            .join(':');

          if (uidHex) {
            this.deviceInfoService.triggerCustomPulsePattern();

            this.onReadingQRNFC(uidHex);
            await CapacitorNfc.stopScanning();
            this.closeNFCListeners();
          }
        },
      );
      this.nativeAppNFCScanStopListner = await CapacitorNfc.addListener(
        'nfcSessionEnd',
        async (event: any) => {
          // The user can click on the overlay dialog opened up by ios, which will stop scanning, we need to close the dialog then
          this.closeDialogBox();
        },
      );

      await CapacitorNfc.startScanning({
        invalidateAfterFirstRead: true,
        alertMessage: 'Place your device on the NFC tag',
        iosSessionType: 'tag',
      });
    } catch (error) {
      this.closeNFCListeners();
    }
  }
  async readNFCWeb() {
    try {
      this.ndefReader = new NDEFReader();

      await this.ndefReader.scan();

      if (this.ndefReadingHandler) {
        this.ndefReader.removeEventListener('reading', this.ndefReadingHandler);
      }

      if (this.ndefReadingErrorHandler) {
        this.ndefReader.removeEventListener(
          'readingerror',
          this.ndefReadingErrorHandler,
        );
      }

      this.ndefReadingHandler = (event: any) => {
        const serialNumber = event?.serialNumber;

        if (!serialNumber) {
          this.updateNFCErrorMsg();
          return;
        }

        this.deviceInfoService.triggerCustomPulsePattern();

        this.onReadingQRNFC(serialNumber);
        this.closeNFCListeners();
      };

      this.ndefReadingErrorHandler = () => {
        this.updateNFCErrorMsg();
        this.closeNFCListeners();
      };

      this.ndefReader.addEventListener('reading', this.ndefReadingHandler);

      this.ndefReader.addEventListener(
        'readingerror',
        this.ndefReadingErrorHandler,
      );
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        this.toasterService.setMessage({
          successMessage: '',
          errorMessage:
            'Guard Tracker needs NFC enabled to scan. Please allow access and try again.',
        });
        this.closeDialogBox(true);
      } else {
        this.updateNFCErrorMsg();
      }
      this.closeNFCListeners();
      throw error;
    }
  }
  updateNFCErrorMsg(msg?: any) {
    this.nfcErrorOutput = msg ? msg : 'Unable to read NFC tag.';
    this.toasterService.setMessage({
      successMessage: '',
      errorMessage: this.nfcErrorOutput,
    });
    this.cdr.detectChanges();
  }
  async turnOffOnTorchNativeApp(action: 'on' | 'off', isRegistering = false) {
    this.devieTorchOn = action == 'on' ? true : false;
    await QRScanner.setTorch({ enabled: this.devieTorchOn });

    if (!isRegistering)
      localStorage.setItem(btoa('lastSavedTorchStatus'), action);
  }
  async pauseResume(action: 'pause' | 'resume') {
    if (action == 'pause') {
      await QRScanner.pause();
    } else {
      await QRScanner.resume();
    }
  }
  async openSettings() {
    this.spinnerService.show('Opening settings.');
    await this.sleep(500);
    this.spinnerService.hide();

    NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS: IOSSettings.App,
    });
  }
}
